/**
 * Unhaze quick add-to-cart for buttons outside Horizon's product form
 * (range cards, room finder results, product-page add-ons).
 *
 * Markup contract: <button data-uh-add data-variant-id="123" [data-qty="1"] [data-added="Added"]>
 *   <span data-label>Add to cart</span>
 * </button>
 * Dispatches the same CartLinesUpdateEvent as Horizon's product form so the cart
 * icon count and cart drawer refresh exactly as they do for the main buy button.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function addToCart(button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const id = button.dataset.variantId;
  const quantity = Number(button.dataset.qty || 1);
  if (!id) return;
  const label = button.querySelector('[data-label]') || button;
  const original = label.textContent;
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity }],
      promise: deferred.promise,
    })
  );
  button.setAttribute('aria-busy', 'true');
  try {
    const body = new FormData();
    body.append('id', id);
    body.append('quantity', String(quantity));
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body }).then((r) => r.json());
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const failed = Boolean(res.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'uh-cart', sourceId: 'uh-cart', itemCount: quantity, sections: res.sections, didError: failed },
    });
    label.textContent = failed ? res.description || res.message || 'Could not add' : button.dataset.added || 'Added to cart';
    button.classList.toggle('is-added', !failed);
  } catch (error) {
    deferred.reject(error);
    label.textContent = 'Could not add. Try again';
  } finally {
    button.removeAttribute('aria-busy');
    setTimeout(() => {
      label.textContent = original;
      button.classList.remove('is-added');
    }, 2600);
  }
}

if (!window.__uhCartBound) {
  window.__uhCartBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-uh-add]') : null;
    if (!target) return;
    event.preventDefault();
    addToCart(target);
  });
}
